import { useId, useState } from 'react';

import { CollapsibleSection } from 'cpool-components/general/CollapsibleSection';
import { get, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AddressFieldsLayout from './AddressElements/AddressFieldsLayout.tsx';
import Land from './AddressElements/Land.tsx';
import WohnhaftSeit from './WohnhaftSeit.tsx';
import { NotificationCountBadge } from '~/features/leadDetails/components/Overview/CustomerData/shared/NotificationCountBadge.tsx';
import { extractErrorPaths } from '~/features/leadDetails/hooks/useTabErrorNotification.ts';
import type { SchemaLivingSituationForm } from '~/features/leadDetails/schemas/living.schema.ts';
import { hasPreviousAddress } from '~/features/leadDetails/utils/hasPreviousAddress.ts';

type Props = {
    antragsteller: 1 | 2;
};

const PreviousAddress: React.FC<Props> = ({ antragsteller }) => {
    const { t } = useTranslation();
    const errorDescriptionId = useId();
    const fieldPath = `antragsteller${antragsteller}.wohnsituation.vorherigeAnschrift` as const;
    const { control, formState } = useFormContext<SchemaLivingSituationForm>();
    const { errors } = useFormState({ control, name: fieldPath });
    // initial values only, so the section doesn't collapse while the user clears fields
    const [defaultOpen] = useState(() => hasPreviousAddress(get(formState.defaultValues, fieldPath)));

    const fieldErrors = get(errors, fieldPath);
    const errorCount = fieldErrors ? extractErrorPaths(fieldErrors).length : 0;
    const title = t('LIVING_SITUATION_DATA.PREVIOUS_ADDRESS');

    return (
        <>
            {errorCount > 0 && (
                <span className="sr-only" id={errorDescriptionId}>
                    {t('CUSTOMER_DATA_ERROR_COUNT', { count: errorCount })}
                </span>
            )}
            <CollapsibleSection
                aria-describedby={errorCount > 0 ? errorDescriptionId : undefined}
                bgClassNames="rounded bg-white px-5 py-2.5"
                defaultOpen={defaultOpen}
                headerClassNames="h-8"
                keepMounted
                testId={`previous-address-applicant${antragsteller}`}
                title={
                    errorCount > 0 ? (
                        <span className="flex items-center gap-2">
                            {title}
                            <NotificationCountBadge ariaHidden count={errorCount} />
                        </span>
                    ) : (
                        title
                    )
                }
                titleClassNames="text-sm font-bold text-midnight"
            >
                <div className="pt-2">
                    <AddressFieldsLayout addressKind="vorherigeAnschrift" antragsteller={antragsteller} titleHidden>
                        <Land addressKind="vorherigeAnschrift" antragsteller={antragsteller} />
                        <WohnhaftSeit antragsteller={antragsteller} addressKind="vorherigeAnschrift" />
                    </AddressFieldsLayout>
                </div>
            </CollapsibleSection>
        </>
    );
};

export default PreviousAddress;
